/*

MIT License

Copyright (c) 2020 react-spring
Copyright (c) 2024 Paragon Component Systems, LLC

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

 */

// noinspection PointlessArithmeticExpressionJS

import React, {
  createContext,
  forwardRef,
  ReactNode,
  RefObject,
  useContext,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { extend, ThreeElement, ThreeElements, useFrame, useThree, Vector3 as Vector3Like } from '@react-three/fiber';
import { Color, ColorRepresentation, Vector2, Vector3 } from 'three';
import { Line2, LineMaterial, LineMaterialParameters, LineSegments2, LineSegmentsGeometry } from 'three-stdlib';

import { assertUnlessInProduction, usePrevious } from '@paragon/client-tools';

type SegmentsProps = LineMaterialParameters & {
  initialSize?: number;
  sizeIncrement?: number;

  lineWidth?: number;
  renderOrder?: number;
  visible?: boolean;
  children: ReactNode;
};

type Api = {
  setDirty: () => void;

  subscribe: (ref: RefObject<SegmentObject>) => () => void;
  subscribeMany: (refs: readonly RefObject<SegmentObject>[]) => () => void;
};

type SegmentRef = RefObject<SegmentObject>;
type SegmentProps = Omit<ThreeElements['segmentObject'], 'ref' | 'start' | 'end' | 'color'> & {
  start: Vector3Like;
  end: Vector3Like;
  color?: ColorRepresentation;
};

const context = createContext<Api | null>(null);

function subscribeSegmentRefs(
  registry: Set<SegmentRef>,
  refs: readonly SegmentRef[],
  onRegistryChange: () => void,
): () => void {
  for (const ref of refs) registry.add(ref);
  onRegistryChange();

  return () => {
    for (const ref of refs) registry.delete(ref);
    onRegistryChange();
  };
}

/**
 * Forked from DREI, with the following changes:
 *   - uses canvas resolution so segment widths are consistent at varying zoom levels
 *   - tracks dirty state so synchronization only need happen on frames where segments have changed
 *   - supports removing segments from the geometry / scene when children are unmounted
 *   - supports "resizing" the underlying geometry by reallocating as needed
 *   - avoids rendering children inside <primitive /> tag which causes event handling issues
 *   - adds an optimized `<ManySegment />` component for registering very large numbers of segments at once
 *   - explicitly manages `instanceCount` to:
 *       a) avoid useless draw calls for empty segment pools
 *       b) avoid useless effort spent drawing full capacity
 */
const Segments = forwardRef<Line2, SegmentsProps>((props, forwardedRef) => {
  const {
    initialSize = 1000,
    sizeIncrement = 500,
    lineWidth = 1.0,
    renderOrder = 0,
    visible = true,
    children,
    ...rest
  } = props;

  const segments = useRef<Set<SegmentRef>>(new Set());

  const canvasSize = useThree((state) => state.size);
  const invalidate = useThree((state) => state.invalidate);
  const resolution = useMemo(() => new Vector2(canvasSize.width, canvasSize.height), [canvasSize]);

  const [line] = useState(() => {
    const line = new LineSegments2(new LineSegmentsGeometry());
    line.geometry.instanceCount = 0;

    return line;
  });

  const [positions] = useState<number[]>(() => Array(initialSize * 6).fill(0));
  const [colors] = useState<number[]>(() => Array(initialSize * 6).fill(0));

  const dirty = useRef(false);
  const previousSegmentCount = useRef(0);

  const api: Api = useMemo(() => {
    const setDirty = () => {
      if (dirty.current) return;

      dirty.current = true;
      invalidate();
    };

    return {
      subscribe: (ref: RefObject<SegmentObject>) => subscribeSegmentRefs(segments.current, [ref], setDirty),
      subscribeMany: (refs: readonly RefObject<SegmentObject>[]) =>
        subscribeSegmentRefs(segments.current, refs, setDirty),
      setDirty,
    };
  }, [invalidate]);

  useFrame(() => {
    if (!dirty.current) return;

    const segmentCount = segments.current.size;

    if (segmentCount * 6 > positions.length) {
      line.geometry.dispose();
      line.geometry = new LineSegmentsGeometry();
      line.geometry.instanceCount = 0;

      const desiredSize = Math.ceil(segmentCount / sizeIncrement) * sizeIncrement;
      const previousLength = positions.length;
      positions.length = colors.length = desiredSize * 6;

      positions.fill(0, previousLength);
      colors.fill(0, previousLength);
    }

    assertUnlessInProduction(positions.length % 6 === 0, 'segments internal state has invalid length');
    assertUnlessInProduction(positions.length === colors.length, 'segments internal state is corrupted');

    let i = 0;
    for (const segmentRef of segments.current) {
      const valueIndex = i++ * 6;
      const segment = segmentRef.current;

      if (segment == null) {
        positions.fill(0, valueIndex, valueIndex + 6);
        colors.fill(0, valueIndex, valueIndex + 6);

        continue;
      }

      positions[valueIndex + 0] = segment.start.x;
      positions[valueIndex + 1] = segment.start.y;
      positions[valueIndex + 2] = segment.start.z;

      positions[valueIndex + 3] = segment.end.x;
      positions[valueIndex + 4] = segment.end.y;
      positions[valueIndex + 5] = segment.end.z;

      colors[valueIndex + 0] = segment.color.r;
      colors[valueIndex + 1] = segment.color.g;
      colors[valueIndex + 2] = segment.color.b;

      colors[valueIndex + 3] = segment.color.r;
      colors[valueIndex + 4] = segment.color.g;
      colors[valueIndex + 5] = segment.color.b;
    }

    if (segmentCount < previousSegmentCount.current) {
      positions.fill(0, segmentCount * 6, previousSegmentCount.current * 6);
      colors.fill(0, segmentCount * 6, previousSegmentCount.current * 6);
    }

    previousSegmentCount.current = segmentCount;

    line.geometry.setColors(colors);
    line.geometry.setPositions(positions);
    line.geometry.instanceCount = segmentCount;

    if (line.material.dashed) line.computeLineDistances();

    dirty.current = false;
  });

  return (
    <>
      <primitive object={line} ref={forwardedRef} renderOrder={renderOrder} visible={visible}>
        <lineMaterial vertexColors resolution={resolution} linewidth={lineWidth} {...rest} />
      </primitive>

      <context.Provider value={api}>{children}</context.Provider>
    </>
  );
});

class SegmentObject {
  color: Color;
  start: Vector3;
  end: Vector3;

  constructor() {
    this.color = new Color('white');
    this.start = new Vector3(0, 0, 0);
    this.end = new Vector3(0, 0, 0);
  }
}

declare module '@react-three/fiber' {
  interface ThreeElements {
    segmentObject: ThreeElement<typeof SegmentObject>;
    lineMaterial: ThreeElement<typeof LineMaterial>;
  }
}

extend({ LineMaterial, SegmentObject });

function normalizeVector3Like(v: Vector3Like): Vector3 {
  return v instanceof Vector3 ? v : new Vector3(...(typeof v === 'number' ? [v, v, v] : v));
}

function normalizeVector3LikeMut(target: Vector3, v: Vector3Like): void {
  if (v instanceof Vector3) target.copy(v);
  else if (typeof v === 'number') target.setScalar(v);
  else target.set(v[0], v[1], v[2]);
}

const Segment = forwardRef<SegmentObject, SegmentProps>((props, forwardedRef) => {
  const api = useContext(context);
  if (!api) throw 'Segment must be used inside Segments component.';

  const ref = useRef<SegmentObject>(null!);
  useImperativeHandle(forwardedRef, () => ref.current, []);
  useLayoutEffect(() => api.subscribe(ref), [api]);

  const values = {
    color: props.color,
    start: normalizeVector3Like(props.start),
    end: normalizeVector3Like(props.end),
  };

  const oldValues = usePrevious(values);

  if (
    oldValues == null ||
    values.color !== oldValues.color ||
    !values.start.equals(oldValues.start) ||
    !values.end.equals(oldValues.end)
  ) {
    api.setDirty();
  }

  return <segmentObject ref={ref} color={values.color} start={values.start} end={values.end} />;
});

type ManySegmentProps = {
  segments: SegmentProps[];
};

/**
 * Optimized component for adding very large numbers of segments to a `<Segments />` instance.
 *
 * The normal `<Segment />` component requires creating an individual React component to register each segment, with all
 *   the overhead that entails. Using `<ManySegment />` lets you avoid all that.
 *
 * Use `<Segment>` for small numbers of dynamic segments where individual control is needed.
 * Use `<ManySegment>` for large batches where performance matters more than granular updates.
 */
function ManySegment(props: ManySegmentProps) {
  const api = useContext(context);
  if (!api) throw 'ManySegment must be used inside Segments component.';

  const { segments: segmentProps } = props;

  const objects = useRef<SegmentObject[]>([]);
  const refs = useRef<RefObject<SegmentObject>[]>([]);

  useLayoutEffect(() => {
    const targetLength = segmentProps.length;
    const currentLength = objects.current.length;

    // grow if needed
    if (targetLength > currentLength) {
      for (let i = currentLength; i < targetLength; i++) {
        objects.current[i] = new SegmentObject();
        refs.current[i] = { current: objects.current[i] };
      }
    }

    // shrink if needed
    else if (targetLength < currentLength) {
      objects.current.length = targetLength;
      refs.current.length = targetLength;
    }

    // sync everything
    for (let i = 0; i < segmentProps.length; i++) {
      const obj = objects.current[i];
      const props = segmentProps[i];

      normalizeVector3LikeMut(obj.start, props.start);
      normalizeVector3LikeMut(obj.end, props.end);

      if (props.color != null) {
        obj.color.set(props.color);
      }
    }

    api.setDirty();

    return api.subscribeMany(refs.current);
  }, [api, segmentProps]);

  return null;
}

export { ManySegment, Segment, Segments };
